import { useRef, useEffect, useState } from "react";
import { Check, X, Trash2, RotateCw } from "lucide-react";

/**
 * Fullscreen signature pad for the driver app.
 * - Renders as a fixed overlay covering the entire viewport (z-index 9999).
 * - Canvas auto-fits to the visible area with a devicePixelRatio aware scale,
 *   so the captured signature is sharp and the on-screen stroke matches the pointer.
 * - Optimized for touch: prevents scroll while drawing, uses pointer events.
 */
export function SignaturePad({ onSave, onCancel }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const drawing = useRef(false);
  const lastPos = useRef(null);
  const hasInkRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  // Fit canvas to container size with devicePixelRatio for crisp strokes
  const fitCanvas = () => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();
    // Preserve current ink so resize (e.g., rotation) doesn't wipe the signature
    const prev = hasInkRef.current ? canvas.toDataURL("image/png") : null;
    canvas.width = Math.floor(rect.width * ratio);
    canvas.height = Math.floor(rect.height * ratio);
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(ratio, ratio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#09090B";
    ctx.lineWidth = 2.8;
    if (prev) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = prev;
    }
  };

  useEffect(() => {
    fitCanvas();
    // Lock page scroll while the pad is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onResize = () => fitCanvas();
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    lastPos.current = getPos(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    if (!hasInkRef.current) { hasInkRef.current = true; setHasInk(true); }
  };
  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const ratio = window.devicePixelRatio || 1;
    ctx.scale(ratio, ratio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#09090B";
    ctx.lineWidth = 2.8;
    hasInkRef.current = false;
    setHasInk(false);
  };

  const save = () => {
    if (!hasInkRef.current) return;
    onSave(canvasRef.current.toDataURL("image/png"));
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-white flex flex-col" data-testid="signature-pad">
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-12 border-b border-zinc-200 shrink-0">
        <button onClick={onCancel} className="flex items-center gap-1 text-zinc-600 text-sm font-bold uppercase" data-testid="signature-cancel">
          <X size={18} /> Annulla
        </button>
        <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">Firma qui sotto</p>
        <button onClick={clear} className="flex items-center gap-1 text-zinc-600 text-sm font-bold uppercase" data-testid="signature-clear">
          <Trash2 size={16} /> Pulisci
        </button>
      </div>

      {/* Canvas area — fills all remaining space */}
      <div ref={containerRef} className="flex-1 relative bg-white">
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full touch-none"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        />
        {!hasInk && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-zinc-300">
            <RotateCw size={28} className="mb-2" />
            <p className="text-xs font-bold uppercase tracking-widest">Ruota il dispositivo per più spazio</p>
            <p className="text-[10px] mt-1">Firma con il dito o lo stilo</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex gap-2 p-3 border-t border-zinc-200 shrink-0">
        <button
          onClick={save}
          disabled={!hasInk}
          className="flex-1 h-12 bg-[#FF5200] text-white text-sm font-bold uppercase rounded-sm flex items-center justify-center gap-2 disabled:opacity-40"
          data-testid="signature-save"
        >
          <Check size={18} /> Conferma Firma
        </button>
      </div>
    </div>
  );
}
